import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReindexAllResult } from '@nucleus-commerce/api-client/platform';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ServerError } from '@/shared/ui/server-error';

import { useReindexAll } from '../hooks/use-reindex-all';

export interface ReindexAllDialogProps {
  onQueued: (result: ReindexAllResult) => void;
}

/** Rebuilds every active and paused store's search index on the worker. */
export function ReindexAllDialog({ onQueued }: ReindexAllDialogProps) {
  const { t } = useTranslation('operations');
  const [open, setOpen] = useState(false);
  const reindex = useReindexAll();

  const confirm = () =>
    reindex.mutate(undefined, {
      onSuccess: (result) => {
        onQueued(result);
        setOpen(false);
      },
    });

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reindex.reset();
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm">
          <RefreshCw aria-hidden />
          {t('search.reindexAll')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('search.confirmTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('search.confirmBody')}</AlertDialogDescription>
        </AlertDialogHeader>
        {reindex.isError ? <ServerError error={reindex.error} /> : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={reindex.isPending}>{t('search.cancel')}</AlertDialogCancel>
          <Button disabled={reindex.isPending} onClick={confirm}>
            {t('search.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
